import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Loader2, X, Mail, MessageSquare, PenLine, CheckCircle, Eye, Clock, Ban, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Signature status chip (Not Sent / Sent / Viewed / Signed / Declined / Expired) ──
const SIG_STATUS = {
  sent: { label: "Sig: Sent", color: "#3B82F6", icon: Clock },
  viewed: { label: "Sig: Viewed", color: "#F59E0B", icon: Eye },
  signed: { label: "Signed", color: "#22C55E", icon: CheckCircle },
  declined: { label: "Declined", color: "#EF4444", icon: Ban },
  expired: { label: "Sig: Expired", color: "#6B7280", icon: AlertTriangle },
  not_sent: { label: "Not Sent", color: "#6B7280", icon: PenLine },
};

export const SignatureStatusBadge = ({ status, showNotSent = false }) => {
  if (!status || (status === "not_sent" && !showNotSent)) return null;
  const cfg = SIG_STATUS[status] || SIG_STATUS.not_sent;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border"
      style={{ background: `${cfg.color}15`, color: cfg.color, borderColor: `${cfg.color}30` }}
      data-testid={`signature-badge-${status}`}
    >
      <Icon className="w-2.5 h-2.5" /> {cfg.label}
    </span>
  );
};

// ── Polling hook: refetch while any doc has a pending signature ──
export const useSignaturePolling = (docs, refetch, intervalMs = 10000) => {
  const ref = useRef(null);
  const pending = (docs || []).some((d) =>
    ["sent", "viewed"].includes(d.signature_display_status)
  );
  useEffect(() => {
    if (pending) {
      ref.current = setInterval(() => refetch(true), intervalMs);
      return () => clearInterval(ref.current);
    }
    return undefined;
  }, [pending, refetch, intervalMs]);
};

// ── Send For Signature Modal (tech portal + owner dashboard via `mode`) ──
export const SendForSignatureModal = ({ doc, onClose, onSent, isDark = true, mode = "tech" }) => {
  const isOwner = mode === "owner";
  const isEstimate = doc.document_type === "estimate";
  const authCfg = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem(isOwner ? "token" : "tech_token")}` },
  });
  const statusUrl = isOwner
    ? `${API}/api/invoices/${doc.invoice_id}/signature-status`
    : `${API}/api/tech/documents/${doc.invoice_id}/signature-status`;
  const sendUrl = isOwner
    ? `${API}/api/invoices/${doc.invoice_id}/send-for-signature`
    : `${API}/api/tech/documents/${doc.invoice_id}/send-for-signature`;
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [viaEmail, setViaEmail] = useState(true);
  const [viaSms, setViaSms] = useState(false);
  const [phoneTarget, setPhoneTarget] = useState("primary");
  const [emailInput, setEmailInput] = useState("");

  const fetchInfo = useCallback(async () => {
    try {
      const res = await axios.get(statusUrl, authCfg());
      setInfo(res.data);
      const c = res.data.customer_contact || {};
      if (!c.has_email && (c.has_phone || c.has_phone2)) { setViaEmail(false); setViaSms(true); }
      if (!c.has_phone && c.has_phone2) setPhoneTarget("secondary");
    } catch {
      toast.error("Failed to load customer contact info");
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.invoice_id, statusUrl]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const contact = info?.customer_contact || {};
  const needsEmailInput = viaEmail && !contact.has_email;
  const canAddEmail = !!info?.can_add_email;

  const validEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());
  const disabled = sending || loading || (!viaEmail && !viaSms) ||
    (needsEmailInput && (!canAddEmail || !validEmail(emailInput))) ||
    (viaSms && !contact.has_phone && !contact.has_phone2);

  const handleSend = async () => {
    setSending(true);
    try {
      const channels = [];
      if (viaEmail) channels.push("email");
      if (viaSms) channels.push("sms");
      const body = { channels, phone_target: phoneTarget };
      if (needsEmailInput && emailInput.trim()) body.customer_email = emailInput.trim();
      const res = await axios.post(sendUrl, body, authCfg());
      toast.success(res.data.message || "Signature request sent!");
      onSent && onSent(res.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send signature request");
    } finally { setSending(false); }
  };

  const boxCls = isDark ? "bg-[#0c0c14] border-white/[0.08]" : "bg-white border-gray-200 shadow-xl";
  const rowCls = (active) => `flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
    active
      ? "border-[#FFCC00]/40 bg-[#FFCC00]/[0.06]"
      : isDark ? "border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04]" : "border-gray-200 bg-gray-50 hover:bg-gray-100"
  }`;

  return (
    <div className={`fixed inset-0 z-[70] flex items-center justify-center p-4 ${isDark ? "bg-black/70" : "bg-black/40"} backdrop-blur-sm`}
      style={{ pointerEvents: "auto" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`w-full max-w-sm max-h-[90dvh] overflow-y-auto rounded-2xl p-5 space-y-4 border ${boxCls}`} data-testid="send-signature-modal">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground font-['Manrope'] flex items-center gap-2">
              <PenLine className="w-4 h-4 text-[#FFCC00]" /> Send for Signature
            </h2>
            <p className="text-muted-foreground text-xs mt-0.5">
              {isEstimate ? "Estimate" : "Invoice"} {doc.invoice_number} · ${(doc.total || 0).toFixed(2)}
              {contact.name ? ` · ${contact.name}` : ""}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="close-signature-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#FFCC00]" /></div>
        ) : (
          <>
            {/* Channel selection */}
            <div className="space-y-2">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Send via</p>
              <label className={rowCls(viaEmail)} data-testid="channel-email">
                <input type="checkbox" checked={viaEmail} onChange={(e) => setViaEmail(e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" />
                <Mail className="w-4 h-4 text-blue-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground font-medium">Email</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {contact.has_email ? contact.email : "No email on file"}
                  </p>
                </div>
              </label>
              <label className={rowCls(viaSms)} data-testid="channel-sms">
                <input type="checkbox" checked={viaSms} onChange={(e) => setViaSms(e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" />
                <MessageSquare className="w-4 h-4 text-emerald-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground font-medium">Text Message (SMS)</p>
                  <p className="text-[11px] text-muted-foreground">
                    {contact.has_phone || contact.has_phone2 ? "Sent securely to the customer" : "No phone on file"}
                  </p>
                </div>
              </label>
            </div>

            {/* SMS contact picker — labels only, numbers stay private */}
            {viaSms && (contact.has_phone || contact.has_phone2) && (
              <div className="space-y-1.5" data-testid="sms-target-picker">
                <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Send text to</p>
                <div className="flex flex-wrap gap-1.5">
                  {contact.has_phone && (
                    <button type="button" onClick={() => setPhoneTarget("primary")}
                      className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${phoneTarget === "primary" ? "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/30" : "text-muted-foreground border-border"}`}
                      data-testid="sms-target-primary">
                      Primary Contact {contact.phone_display ? `(${contact.phone_display})` : ""}
                    </button>
                  )}
                  {contact.has_phone2 && (
                    <button type="button" onClick={() => setPhoneTarget("secondary")}
                      className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${phoneTarget === "secondary" ? "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/30" : "text-muted-foreground border-border"}`}
                      data-testid="sms-target-secondary">
                      Secondary Contact {contact.phone2_display ? `(${contact.phone2_display})` : ""}
                    </button>
                  )}
                  {contact.has_phone && contact.has_phone2 && (
                    <button type="button" onClick={() => setPhoneTarget("both")}
                      className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${phoneTarget === "both" ? "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/30" : "text-muted-foreground border-border"}`}
                      data-testid="sms-target-both">
                      Both
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Inline customer email capture */}
            {needsEmailInput && (
              canAddEmail ? (
                <div className="space-y-1.5" data-testid="inline-email-capture">
                  <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">Customer Email</p>
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="customer@email.com"
                    className={`w-full h-10 px-3 rounded-xl text-sm border focus:outline-none ${isDark ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#FFCC00]/30" : "bg-white border-gray-300 text-gray-900"}`}
                    data-testid="customer-email-input"
                  />
                  <p className="text-[10px] text-muted-foreground">Will be saved to this customer's profile before sending</p>
                </div>
              ) : (
                <p className="text-[11px] text-amber-500 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5" data-testid="email-permission-note">
                  This customer has no email on file and you don't have permission to add one. Send by SMS, or ask your office to add the email.
                </p>
              )
            )}

            <Button
              onClick={handleSend}
              disabled={disabled}
              className="w-full h-11 rounded-xl bg-[#FFCC00] hover:bg-[#E6B800] text-black font-semibold"
              data-testid="send-signature-submit"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PenLine className="w-4 h-4 mr-2" />}
              {sending ? "Sending..." : "Send Signature Request"}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              Customer gets a secure link to review, sign electronically, and submit
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SendForSignatureModal;
